import React, { useCallback, useMemo, useState } from 'react';
import { MessageSquare } from 'react-feather';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { Person } from 'types';
import ScrollableUncontrolledPopover from 'views/Widgets/ScrollableUncontrolledPopover';
import {
  CreateCommentHandler,
  EditCommentHandler,
  ThreadedCalibrationComment,
} from '../types';
import { flattenThreadedComments } from '../utils/comments';
import CalibrationCommentsPopoverContent, {
  ToggleVisibilityHandler,
} from './CalibrationCommentsPopoverContent';
import CommentsModal from './CommentsModal';

interface Props {
  participant: Person;
  surveyResponseId: number;
  readOnly: boolean;
  calibrationComments: ThreadedCalibrationComment[];
  showPrivateComments: boolean;
  onToggleShowPrivate: () => void;
  onPostComment: CreateCommentHandler;
  onToggleVisibility?: ToggleVisibilityHandler;
  onEditComment?: EditCommentHandler;
  currentUserId?: number;
  isViewAdmin?: boolean;
}

const CommentsCell: React.FC<Props> = ({
  participant,
  readOnly,
  surveyResponseId,
  calibrationComments,
  showPrivateComments,
  onToggleShowPrivate,
  onPostComment,
  onToggleVisibility,
  onEditComment,
  currentUserId,
  isViewAdmin = false,
}) => {
  const { formatMessage, formatNumber } = useIntl();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const flatComments = useMemo(
    () => flattenThreadedComments(calibrationComments),
    [calibrationComments]
  );

  const totalComments = flatComments.length;
  const targetId = `calibration-comments-${surveyResponseId}`;

  const countLabel = formatMessage(
    {
      id: 'app.views.widgets.dashboards.calibration_dashboard.participants.column.comments.count',
      defaultMessage:
        '{count, plural, =0 {No comments} one {{count} comment} other {{count} comments}}',
    },
    { count: totalComments }
  );

  const toggleModal = useCallback(
    () => setIsModalOpen((value) => !value),
    [setIsModalOpen]
  );

  return (
    <div className="d-flex align-items-center">
      {isModalOpen && (
        <CommentsModal
          comments={calibrationComments}
          isOpen={isModalOpen}
          toggle={toggleModal}
          participant={participant}
          showPrivateComments={showPrivateComments}
          onToggleShowPrivate={onToggleShowPrivate}
          canAddCalibrationComment={!readOnly}
          onSubmitComment={onPostComment}
          onToggleVisibility={onToggleVisibility}
          onEditComment={onEditComment}
          currentUserId={currentUserId}
          isViewAdmin={isViewAdmin}
          surveyResponseId={surveyResponseId}
        />
      )}
      <Button
        type="button"
        color="link"
        size="sm"
        className="px-0 text-body fw-normal text-decoration-none d-inline-flex align-items-center gap-2"
        id={targetId}
        aria-label={countLabel}
        onClick={toggleModal}
      >
        <span>{formatNumber(totalComments)}</span>
        <MessageSquare size={14} aria-hidden="true" />
      </Button>
      {totalComments > 0 && (
        <ScrollableUncontrolledPopover
          placement="left"
          trigger="hover"
          autohide={false}
          target={targetId}
          className={'popover-variable-lg'}
        >
          <CalibrationCommentsPopoverContent
            participant={participant}
            recommendation={'all'}
            comments={calibrationComments}
            showPrivateComments={showPrivateComments}
            onToggleShowPrivate={onToggleShowPrivate}
            canAddComment={!readOnly}
            onSubmitComment={onPostComment}
            onToggleVisibility={onToggleVisibility}
            currentUserId={currentUserId}
            isViewAdmin={isViewAdmin}
            variant={'popover'}
            surveyResponseId={surveyResponseId}
          ></CalibrationCommentsPopoverContent>
        </ScrollableUncontrolledPopover>
      )}
    </div>
  );
};

export default React.memo(CommentsCell);
